import { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { CONTENT } from "@/content/contentPages";
import { EmptyState } from "@/components/shared";
import { FileQuestion, ArrowRight, ChevronRight } from "lucide-react";

// bold **...** inline
function Inline({ text }) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((p, i) =>
        p.startsWith("**") && p.endsWith("**")
          ? <strong key={i} className="font-semibold text-ink">{p.slice(2, -2)}</strong>
          : <span key={i}>{p}</span>
      )}
    </>
  );
}

function Block({ b, nav }) {
  switch (b.t) {
    case "h":
      return <h2 className="mt-12 text-2xl font-bold text-ink">{b.text}</h2>;
    case "p":
      return <p className="mt-4 text-base leading-relaxed text-slate-300"><Inline text={b.text} /></p>;
    case "quote":
      return (
        <blockquote className="mt-6 border-l-2 border-teal-500 pl-5 text-lg leading-relaxed text-slate-200">
          <Inline text={b.text} />
        </blockquote>
      );
    case "ul":
      return (
        <ul className="mt-4 space-y-3">
          {b.items.map((it, i) => (
            <li key={i} className="flex gap-3 text-base leading-relaxed text-slate-300">
              <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-teal-400" aria-hidden="true" />
              <span><Inline text={it} /></span>
            </li>
          ))}
        </ul>
      );
    case "note":
      return (
        <div className="mt-6 rounded-xl border border-coral-500/30 bg-coral-500/5 p-4 text-sm leading-relaxed text-coral-500">
          <Inline text={b.text} />
        </div>
      );
    case "qa":
      return (
        <div className="mt-6 space-y-4">
          {b.items.map((qa, i) => (
            <div key={i} className="card-surface p-5" data-testid="faq-item">
              <p className="font-heading text-lg font-semibold text-ink">{qa.q}</p>
              <p className="mt-2 text-base leading-relaxed text-slate-300"><Inline text={qa.a} /></p>
            </div>
          ))}
        </div>
      );
    case "cta":
      return (
        <div className="mt-8 flex flex-wrap gap-3">
          {b.items.map((c, i) => (
            <button key={i} data-testid="content-cta" onClick={() => nav(c.to)}
              className={i === 0
                ? "btn-teal inline-flex items-center gap-2 text-sm"
                : "inline-flex items-center gap-2 rounded-full border border-[#DDDAD1] px-6 py-3 text-sm text-slate-200 hover:border-teal-400/40"}>
              {c.label} <ArrowRight className="h-4 w-4" />
            </button>
          ))}
        </div>
      );
    case "links":
      return (
        <div className="mt-10 flex flex-wrap gap-x-6 gap-y-2 border-t border-[#DDDAD1] pt-6 text-sm">
          <span className="text-slate-500">Related:</span>
          {b.items.map((l, i) => (
            <Link key={i} to={`/p/${l.slug}`} className="text-teal-400 hover:text-teal-300">{l.label}</Link>
          ))}
        </div>
      );
    default:
      return null;
  }
}

export default function ContentPage({ slug: slugProp }) {
  const { slug: slugParam } = useParams();
  const slug = slugProp || slugParam;
  const nav = useNavigate();
  const page = CONTENT[slug];

  useEffect(() => {
    if (page) {
      document.title = page.seoTitle || "iGamingTruth";
      let m = document.querySelector('meta[name="description"]');
      if (!m) { m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); }
      m.setAttribute("content", page.metaDescription || "");
    }
    window.scrollTo(0, 0);
    return () => { document.title = "iGamingTruth"; };
  }, [slug, page]);

  if (!page) {
    return (
      <main className="mx-auto max-w-3xl px-5 py-20 lg:px-8">
        <EmptyState icon={FileQuestion} title="Page not found" subtitle="This page does not exist yet."
          action={<Link to="/" className="btn-teal mt-2 text-sm">Back home</Link>} />
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-3xl px-5 py-14 lg:px-8">
      {page.overline && <p className="eyebrow mb-4">{page.overline}</p>}
      <h1 className="text-4xl font-bold leading-tight sm:text-5xl">{page.h1}</h1>
      <article>
        {page.blocks.map((b, i) => <Block key={i} b={b} nav={nav} />)}
      </article>
    </main>
  );
}
